import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { subscriptionAPI } from '~/api/subscriptionAPI';
import { type TSubscription, type TSubscriptionPlan } from '~/types';

const fetchPlans = createAsyncThunk('subscription/fetchPlans', async () => {
  return await subscriptionAPI.getPlans();
});

const fetchMySubscription = createAsyncThunk(
  'subscription/fetchMySubscription',
  async () => {
    return await subscriptionAPI.getMySubscription();
  },
);

const verifyPayment = createAsyncThunk(
  'subscription/verifyPayment',
  async ({
    txHash,
    walletAddress,
  }: {
    txHash: string;
    walletAddress: string;
  }) => {
    return await subscriptionAPI.verify(txHash, walletAddress);
  },
);

export type SubscriptionState = {
  subscription: TSubscription | null;
  plans: TSubscriptionPlan[];
  loading: boolean;
  verifying: boolean;
  error: string | null;
};

const initialState: SubscriptionState = {
  subscription: null,
  plans: [],
  loading: false,
  verifying: false,
  error: null,
};

export const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchPlans.fulfilled, (state, action) => {
        state.plans = action.payload;
      })
      .addCase(fetchMySubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMySubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.subscription = action.payload;
      })
      .addCase(fetchMySubscription.rejected, (state) => {
        state.loading = false;
      })
      .addCase(verifyPayment.pending, (state) => {
        state.verifying = true;
        state.error = null;
      })
      .addCase(verifyPayment.fulfilled, (state, action) => {
        state.verifying = false;
        state.subscription = action.payload;
      })
      .addCase(verifyPayment.rejected, (state, action) => {
        state.verifying = false;
        state.error = action.error.message ?? 'Payment verification failed';
      });
  },
  selectors: {
    subscription: (state) => state.subscription,
    plans: (state) => state.plans,
    loading: (state) => state.loading,
    verifying: (state) => state.verifying,
    error: (state) => state.error,
    isActive: (state) =>
      state.subscription !== null &&
      state.subscription.status === 'active' &&
      new Date(state.subscription.expiresAt) > new Date(),
  },
});

export const subscriptionThunks = {
  fetchPlans,
  fetchMySubscription,
  verifyPayment,
};

export const subscriptionSelectors = subscriptionSlice.selectors;
